import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table } from "antd";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { fleetThunks } from "fleet/presentation/store/fleetSlice";
import { useAppDispatch, useAppSelector } from "core/presentation/store/hooks";
import ATitle from "core/presentation/components/Title";
import Loader from "core/presentation/components/Loader";
import "leaflet/dist/leaflet.css";
import currentLocation from "../../../../../assets/images/driver-location.png";
import currentGreenLocation from "../../../../../assets/images/green-driver-location2.png";
import { color } from "theme/Color";
import { DotIcon } from "../../../../../assets/icons/DotIcon";
import { calculateMapCenterAndZoom, getIconSize, isTripRunning } from "core/presentation/utils/Helper";
import { Map as LeafletMap } from "leaflet";

const CLUSTER_RADIUS_METERS = 20;

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

interface VehicleCluster {
  id: string;
  vehicles: any[];
  latitude: number;
  longitude: number;
  avgSpeed: number;
  heading: number;
  isAnyRunning: boolean;
}

function clusterVehicles(vehicles: any[]): VehicleCluster[] {
  const visited = new Set<number>();
  const clusters: VehicleCluster[] = [];

  vehicles.forEach((v, i) => {
    if (visited.has(i)) return;
    const group = [v];
    visited.add(i);

    vehicles.forEach((u, j) => {
      if (visited.has(j)) return;
      if (haversineMeters(v.latitude ?? 0, v.longitude ?? 0, u.latitude ?? 0, u.longitude ?? 0) <= CLUSTER_RADIUS_METERS) {
        group.push(u);
        visited.add(j);
      }
    });

    const avgLat = group.reduce((s, x) => s + (x.latitude ?? 0), 0) / group.length;
    const avgLng = group.reduce((s, x) => s + (x.longitude ?? 0), 0) / group.length;
    const avgSpeed = group.reduce((s, x) => s + (x.speed > 0 ? x.speed : 0), 0) / group.length;

    clusters.push({
      id: `cluster-${i}`,
      vehicles: group,
      latitude: avgLat,
      longitude: avgLng,
      avgSpeed,
      heading: group[0]?.heading ?? 0,
      isAnyRunning: group.some((x) => isTripRunning(x)),
    });
  });

  return clusters;
}

function createClusterMarker(iconUrl: string, heading: number, zoom: number, avgSpeed: number, vehicleCount: number): L.DivIcon {
  const [iconWidth, iconHeight] = getIconSize(zoom);
  const badgeSize = Math.max(Math.floor(zoom * 1.2), 16);
  const fontSize = Math.max(Math.floor(zoom * 0.6), 10);

  return new L.DivIcon({
    html: `
      <div style="position:relative;width:${iconWidth}px;height:${iconHeight}px;">
        ${
          vehicleCount === 1
            ? `
        <!-- Speed badge (top-right, green) — single vehicle only -->
        <div style="
          position:absolute;top:-${badgeSize / 4}px;right:-${badgeSize / 4}px;
          background:#52c41a;color:#1a5200;
          width:${badgeSize}px;height:${badgeSize}px;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          font-size:${fontSize}px;font-weight:500;z-index:1000;
          border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.2);">
          ${Math.round(avgSpeed)}
        </div>`
            : `
        <!-- Count badge (top-left, blue) — cluster only -->
        <div style="
          position:absolute;top:-${badgeSize / 4}px;left:-${badgeSize / 4}px;
          background:#1677ff;color:#e6f1fb;
          width:${badgeSize}px;height:${badgeSize}px;border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          font-size:${fontSize}px;font-weight:500;z-index:1000;
          border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.2);">
          ${vehicleCount}
        </div>`
        }
        <img
          src="${iconUrl}"
          style="width:100%;height:100%;display:block;
            transform:rotate(${0}deg);transform-origin:center center;"
        />
      </div>`,
    className: "",
    iconSize: [iconWidth, iconHeight],
    iconAnchor: [iconWidth / 2, iconHeight / 2],
  });
}

const VehicleLocations = () => {
  const dispatch = useAppDispatch();
  const { vehiclesForTracking } = useAppSelector((state) => state.fleet);

  const [mapCenter, setMapCenter] = useState({ lat: 27.692087, lng: 85.304582 });
  const [zoomLevel, setZoomLevel] = useState(12);
  const mapRef = useRef<LeafletMap | null>(null);

  useEffect(() => {
    dispatch(fleetThunks.getAllVehiclesForLiveTracking());
  }, [dispatch]);

  const calculateZoom = useCallback(() => {
    const { center, zoom } = calculateMapCenterAndZoom(vehiclesForTracking);
    setZoomLevel(zoom);
    setMapCenter(center);
  }, [vehiclesForTracking]);

  useEffect(() => {
    calculateZoom();
  }, [calculateZoom]);

  const ZoomHandler = ({ onZoomChange }: { onZoomChange: (zoom: number) => void }) => {
    useMapEvents({
      zoomend: (event) => {
        onZoomChange(event.target.getZoom());
      },
    });
    return null;
  };

  const clusters = useMemo(() => clusterVehicles(vehiclesForTracking ?? []), [vehiclesForTracking]);

  const clusterTableColumns = [
    {
      title: "#",
      key: "index",
      width: 40,
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    { title: "Driver", dataIndex: "driverName", key: "driverName", width: 160 },
    {
      title: "Vehicle",
      dataIndex: "vehicleNumber",
      key: "vehicleNumber",
      width: 180,
      render: (vehicleNumber: string, record: any) => {
        const running = isTripRunning(record);
        return (
          <span style={{ position: "relative", display: "inline-block", paddingRight: 14 }}>
            {vehicleNumber}
            <span
              style={{
                position: "absolute",
                top: -4,
                right: -2,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: running ? "#52c41a" : "#fa8c16",
                border: "1.5px solid #fff",
                display: "inline-block",
              }}
            />
          </span>
        );
      },
    },
    {
      title: "Owner",
      dataIndex: "ownerName",
      key: "ownerName",
      width: 160,
      render: (val: string) => val ?? "—",
    },
    {
      title: "Speed",
      key: "speed",
      render: (record: any) => `${Math.round(record.speed > 0 ? record.speed : 0)} km/h`,
    },
  ];

  if (!vehiclesForTracking) return <Loader />;

  return (
    <>
      <div
        style={{
          padding: "30px",
          border: "1px solid #EAECF0",
          margin: "20px 0",
          borderRadius: "8px",
          height: "calc(85vh - 200px)",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <ATitle content="Vehicle Location" fontSize={14} fontWeight={600} marginTop="0" lineHeight={"30px"} textColor={color.DARK} />
        </div>

        <MapContainer
          center={mapCenter}
          zoom={zoomLevel - 2}
          scrollWheelZoom={true}
          style={{ width: "100%", height: "80%", borderRadius: "8px" }}
          ref={(map) => {
            mapRef.current = map;
            if (map) setZoomLevel(map.getZoom());
          }}
        >
          <ZoomHandler onZoomChange={(z) => setZoomLevel(z)} />
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {clusters.map((cluster) => {
            const runningCount = cluster.vehicles.filter((v) => isTripRunning(v)).length;
            const idleCount = cluster.vehicles.length - runningCount;
            const iconUrl = runningCount >= idleCount ? currentGreenLocation : currentLocation;

            return (
              <Marker key={cluster.id} position={{ lat: cluster.latitude, lng: cluster.longitude }} icon={createClusterMarker(iconUrl, cluster.heading, zoomLevel, cluster.avgSpeed, cluster.vehicles.length)}>
                <Popup minWidth={cluster.vehicles.length > 1 ? 780 : 220}>
                  {cluster.vehicles.length === 1 ? (
                    // ── Single vehicle popup ───────────────────────────────
                    <div>
                      <p>
                        <strong>Driver:</strong> {cluster.vehicles[0]?.driverName}
                      </p>
                      <p>
                        <strong>Vehicle:</strong> {cluster.vehicles[0]?.vehicleNumber}
                      </p>
                    </div>
                  ) : (
                    // ── Cluster popup: scrollable table ───────────────────
                    <>
                      <p style={{ fontWeight: 600, marginBottom: 8 }}>{cluster.vehicles.length} vehicles in this area</p>
                      <Table dataSource={cluster.vehicles} columns={clusterTableColumns} rowKey="vehicleId" size="small" pagination={false} scroll={{ x: 700, y: 300 }} />
                    </>
                  )}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        <div style={{ marginTop: "10px", textAlign: "center" }}>
          <span style={{ color: `${color.SHADOW_ORANGE}`, marginRight: "20px" }}>
            <DotIcon />
            <span
              style={{
                color: `${color.DARK}`,
                fontSize: "12px",
                fontWeight: 500,
                marginLeft: "10px",
              }}
            >
              Assigned: {vehiclesForTracking?.filter((vehicle: any) => !!vehicle?.tripId)?.length ?? 0}
            </span>
          </span>
          <span style={{ color: `${color.GREEN}` }}>
            <DotIcon />
            <span
              style={{
                color: `${color.DARK}`,
                fontSize: "12px",
                fontWeight: 500,
                marginLeft: "10px",
              }}
            >
              Running: {vehiclesForTracking?.length}
            </span>
          </span>
        </div>
      </div>
    </>
  );
};

export default VehicleLocations;
